var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a, _b;
import PubSub from 'pubsub-js';
import Cookies from 'js-cookie';
import { platform_base_url } from './config';
import { post } from './request';
import { trackEvent } from './track';
import { isBrowser } from './ssr';
export var CURRENCY_SYMBOL = typeof window !== 'undefined' && ((_b = (_a = window.Shopify) === null || _a === void 0 ? void 0 : _a.currency) === null || _b === void 0 ? void 0 : _b.active) === 'GBP'
    ? '£'
    : '$';
/**
 * Splits an array into two parts at the specified delimiter.
 * @param {array} list List to split
 * @param {*} delimiter Item used to delimit `list`
 * @returns {array} An array with two subarrays, representing the elements before and elements after `delimiter`.
 */
export var splitArray = function (list, delimiter) {
    if (!list || !Array.isArray(list))
        throw new Error('Calls to #splitArray require a list to split.');
    var delimiterIndex = list.indexOf(delimiter);
    if (delimiterIndex < 0) {
        delimiterIndex = list.length;
    }
    return [list.slice(0, delimiterIndex), list.slice(delimiterIndex + 1)];
};
export var replaceAllSpaces = function (string, replacement) {
    if (string === void 0) { string = ''; }
    if (replacement === void 0) { replacement = '-'; }
    return string.replace(/ /g, replacement);
};
/**
 * A pluralization utility
 * @param {num} amount - the quantity of the item
 * @param {string} item - the object to be pluralized. Must be passed as a singluar object
 * @param {string} modifier [modifier=s] - the plural modifier. Some words might need an 'es' such as tomato vs tomatoes
 * @param {string} override [override=null] - A plural modifier for special cases such as person vs people
 */
export var pluralize = function (amount, item, modifier, override) {
    if (modifier === void 0) { modifier = 's'; }
    if (override === void 0) { override = null; }
    if (amount < 0)
        throw new Error('The amount must be a positive integer');
    if (amount === 1) {
        if (modifier && item.endsWith(modifier)) {
            return item.slice(0, item.length - modifier.length);
        }
        return item;
    }
    if (override) {
        return "".concat(override);
    }
    return "".concat(item).concat(modifier);
};
/**
 * Determines if a string is a hex code
 * @param {string} string - input string to test
 */
export var isHexCode = function (string) {
    return string.includes('#') && (string.length === 7 || string.length === 4);
};
/**
 * Traps focus within an element when a user presses tab
 * @param {SyntheticBaseEvent} e - the keyboard event from the element to trap focus in
 */
export var trapFocusInElement = function (e, targetNode) {
    if (targetNode === void 0) { targetNode = null; }
    var focusableEls = (targetNode || e.currentTarget).querySelectorAll('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])');
    var firstFocusableEl = focusableEls[0];
    var lastFocusableEl = focusableEls[focusableEls.length - 1];
    var KEYCODE_TAB = 9;
    var isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;
    if (isTabPressed) {
        if (e.shiftKey) {
            if (document.activeElement === firstFocusableEl) {
                lastFocusableEl.focus();
                e.preventDefault();
            }
        }
        else if (document.activeElement === lastFocusableEl) {
            firstFocusableEl.focus();
            e.preventDefault();
        }
    }
};
/**
 * Uses an <img /> to detect if a src renders a valid image
 *
 * @param {String} [src = '']
 * @returns {Promise} - a promise object that resolves to true or false if the image loads or errors, respectively
 */
export var waitForImage = function (src) {
    if (src === void 0) { src = ''; }
    var img = new Image();
    img.style.display = 'none';
    img.style.height = '0';
    img.style.width = '0';
    var image = new Promise(function (resolve) {
        img.onload = function () {
            resolve(true);
        };
        img.onerror = function () {
            resolve(false);
        };
        img.src = src;
    })
        .finally(function () { return img.remove(); })
        .catch(function () { return false; });
    return image;
};
/**
 * Detect if the current device is touch enabled
 *
 * WARNING: We should always try a `css` media query solution when possible.
 * However, in cases when that isn't possible, this function is available.
 */
export var detectTouch = function () {
    var _a;
    if (!isBrowser())
        return false;
    return (_a = window === null || window === void 0 ? void 0 : window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(hover: none), (pointer: coarse)').matches;
};
/**
 * Sanitizes a string to contain only numbers and formats those numbers into a beautified phone number.
 * @param {String} value String to format
 * @param {String} [locale=US] Desired locale for formatting (ISO 3166-1 alpha-2)
 * @returns {String} A formatted phone number
 */
export var formatPhone = function (value, locale) {
    if (locale === void 0) { locale = 'US'; }
    if (locale === 'US') {
        if (!value)
            return value;
        var phoneNumber = value.replace(/[^\d]/g, '');
        var phoneNumberLength = phoneNumber.length;
        if (phoneNumberLength < 4)
            return phoneNumber;
        if (phoneNumberLength < 7) {
            return "(".concat(phoneNumber.slice(0, 3), ") ").concat(phoneNumber.slice(3));
        }
        return "(".concat(phoneNumber.slice(0, 3), ") ").concat(phoneNumber.slice(3, 6), "-").concat(phoneNumber.slice(6, 10));
    }
    return value;
};
/**
 *
 * @param {Object} variant Storefront ProductVariant object, contains different versions of a product
 * @returns {String} Formatted relative path to PDP with exact size and variant
 */
export var getProductVariantUrl = function (variant) {
    if (!variant) {
        return null;
    }
    var handle = variant.product.handle, selectedOptions = variant.selectedOptions;
    var options = selectedOptions.reduce(function (curr, option) {
        var name = option.name.replace(/\s+/g, '+').toLowerCase();
        var replacementCharacter = name === 'color' ? '-' : '+';
        var value = option.value
            .replace(/\s+/g, replacementCharacter)
            .toLowerCase();
        var query = "".concat(name === 'color' || name === 'value' ? 'variant' : name, "=").concat(value, "&");
        return curr + query;
    }, '');
    var queryString = options.slice(0, -1);
    return "/products/".concat(handle, "?").concat(queryString);
};
/**
 * Transform forms an array of shopify product tags to an object for easy lookup
 * @param {Array} tags array of colon delimited shopify product tags
 */
export var transformTagsIntoProperties = function (tags) {
    if (tags === void 0) { tags = []; }
    return tags.reduce(function (prev, curr) {
        var _a;
        var separated = curr.split(':');
        var key = separated[0];
        var value = separated.slice(1, separated.length).join(':');
        return __assign(__assign({}, prev), (_a = {}, _a[key] = __spreadArray(__spreadArray([], (prev[key] || []), true), [value.length > 0 ? value : key], false), _a));
    }, {});
};
/**
 * Generates a top level domain for cross app cookies
 * @param {String} hostname current url hostname
 * @returns {{ .bombas.co.uk || .bombas.com || localhost || .bombas.fyi }}
 */
export var getCookieDomain = function () {
    var hostname = window.location.hostname;
    var rootDomain = hostname.substring(hostname.indexOf('bombas'));
    return "".concat(rootDomain !== 'localhost' ? '.' : '').concat(rootDomain);
};
/**
 * Validates an email is a customer with 0 purchases and applies new customer
 * coupon upon success
 * @param {String} couponCode
 * @param {String} email
 */
export var applyNewCustomerCoupon = function (couponCode, email) {
    return post("".concat(platform_base_url, "/v1/marketing/email-sign-up"), {
        contact: { email: email }
    })
        .then(function () {
        PubSub.publish('cart.discountApply', couponCode);
        Cookies.set('optimizelyNewCustomerOfferEmailCaptureSuccess', 'true', {
            domain: getCookieDomain()
        });
        trackEvent({
            event: 'Mailing List Sign-Up',
            customer: { returning: false }
        });
    })
        .catch(function (e) {
        trackEvent({
            event: 'Mailing List Sign-Up Failure',
            customer: { returning: true }
        });
        throw new Error(e);
    });
};
/**
 * Determines the error policy based on the provided query. This function is intended to be used
 * only within getServerSideProps functions.
 *
 * @param {Object} query - The query object from the context of getServerSideProps, which may have a `gql_error` property.
 * @param {string} query.gql_error - The GraphQL error status. If it's 'all', the function will return 'all'.
 *
 * @returns {string} - Returns 'none' if the query object is empty or if `gql_error` is not 'all'.
 * Otherwise, returns 'all'.
 */
export var getErrorPolicy = function (query) {
    if (query === void 0) { query = {}; }
    return Object.keys(query).length === 0
        ? 'none'
        : (query === null || query === void 0 ? void 0 : query.gql_error) === 'all'
            ? 'all'
            : 'none';
};
/**
 * Returns empty string if no `html` provided.
 * Otherwise it strips out all HTML tags and returns it.
 *
 * @param {string|null|undefined} html - HTML string input
 * @returns {string} - string without HTML tags
 */
export var stripHTMLTags = function (html) {
    if (!html)
        return '';
    return html.replace(/<[^>]*>?/gm, '');
};
/**
 * Clean and easy way to conditionally add an item to an array or object.
 * Intended to be paired with spread syntax.
 * @example [...insertIf(true, [1]), 2, 3] => [1, 2, 3]
 * @example {...insertIf(true, { hello: 'world' }), goodbye: 'earth' } => { hello: 'world', goodbye: 'earth' }
 */
export var insertIf = function (condition, result) {
    if (condition)
        return result;
    if (Array.isArray(result))
        return [];
    return {};
};
/**
 *  Compares two objects for deep equality.
 */
export var deepEqual = function (value1, value2) {
    if (value1 === value2)
        return true;
    // If either is not an object or is null, they are not equal
    if (value1 === null ||
        value2 === null ||
        typeof value1 !== 'object' ||
        typeof value2 !== 'object') {
        return false;
    }
    var keys1 = Object.keys(value1);
    var keys2 = Object.keys(value2);
    if (keys1.length !== keys2.length)
        return false;
    if (keys1.some(function (key) {
        return !keys2.includes(key) ||
            !deepEqual(value1[key], value2[key]);
    })) {
        return false;
    }
    return true;
};
/**
 * Client-side routing for hostnames on the same domain.
 *
 * @param router Next.js router instance
 * @param targetUrl URL to navigate to
 */
export var handleNavigate = function (router, targetUrl) {
    var url = isBrowser() && new URL(window.location.href);
    var _a = url instanceof URL ? url : {}, _b = _a.hostname, hostname = _b === void 0 ? '' : _b, _c = _a.origin, origin = _c === void 0 ? '' : _c;
    // some links in Contentful are relative urls, so we need a check for that
    var isRelativeUrl = /^\/(?!\/)/.test(targetUrl);
    // create a new URL object to get the hostname if path exists
    // if the url is relative, we need to use the origin of the current page
    // otherwise, we can use the url as is
    var pathHostname = (isRelativeUrl
        ? new URL(targetUrl, origin)
        : new URL(targetUrl)).hostname;
    // client-side routing if the path is on the same hostname
    // otherwise, navigate via window.location
    if (pathHostname === hostname) {
        router.push(targetUrl);
    }
    else {
        window.location.href = targetUrl;
    }
};
export default {};
